import React, { useState, useEffect } from "react";
import AuthUserContext from "./context";
import { useFirebase } from "../Firebase";
import { getQueryArgs } from "../../helpFunctions/general";
import * as Sentry from "@sentry/react";
import { useLocation } from "react-router-dom";
import * as ROUTES from "../../constants/routes";
import { useNavigateCompat } from "../../helpFunctions/hooks";

const withAuthentication = Component => {
  const WithAuthentication = props => {
    const [authUser, setAuthUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signingIn, setSigningIn] = useState(false);

    const navigate = useNavigateCompat();
    const location = useLocation();
    const firebase = useFirebase();

    const updateUser = user => {
      setAuthUser(prevAuthUser => ({ ...prevAuthUser, ...user }));
    };

    useEffect(() => {
      const span = Sentry.startInactiveSpan({ name: "Firebase Auth", op: "db" });
      // Start listening to firebase auth updates (once on mount)
      const listener = firebase.onAuthUserListener(
        authUser => {
          setAuthUser(authUser);
          setLoading(false);
          span.end();
          if (!authUser?.userId) {
            Sentry.setUser(null);
          } else {
            // Only report email for internal users for GDPR compliance
            const email = authUser.email?.endsWith("fictivereality.com") ? authUser.email : null;
            Sentry.setUser({
              id: authUser.userId,
              memberOf: authUser.claims?.memberOf,
              emailVerified: authUser.claims?.email_verified,
              ip_address: null,
              email: email,
            });
          }
          console.log(`Authenticated as ${authUser?.userId}`);
        },
        () => {
          span.end();
          Sentry.setUser(null);
          setAuthUser(null);
          setLoading(false);
        }
      );

      return () => {
        listener();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firebase]);

    useEffect(() => {
      // If invite query parameter at initial load, redirect
      const queryArgs = getQueryArgs(location);
      const invite = queryArgs?.get("invite");
      if (invite) {
        navigate(
          {
            pathname: ROUTES.SIGN_IN,
            search: location.search,
          },
          {
            // nextUrl includes the invite query parameter but it will be removed at signin page
            state: { nextUrl: `${location.pathname}${location.search}` },
          }
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <AuthUserContext.Provider value={{ authUser, loading, signingIn, updateUser, setSigningIn }}>
        <Component {...props} authUser={loading || signingIn ? null : authUser} />
      </AuthUserContext.Provider>
    );
  };

  return WithAuthentication;
};

export default withAuthentication;
